import { CSSObject } from "@emotion/core";
import styled from "@emotion/styled";
import { motion } from "@smartsheet/design-tokens";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import { createFocusRingStyles } from "../../../../util/createFocusRingStyles";
import { getInputFieldHeightValue, InputFieldBaseProps } from "../../../../util/inputFieldBaseProps";
import { Clickable } from "../../../clickable";
import { formInputTheme } from "../../../textinput/styled/formInputTheme";
import { selectV2Theme } from "../selectV2Theme";

interface ActionButtonBaseProps extends InputFieldBaseProps {
    themeMode: RequiredThemeKeys;
}

export const ActionButtonBaseCss = ({ themeMode, inputFieldHeight }: ActionButtonBaseProps): CSSObject => ({
    display: "inline-flex",
    alignItems: "center",
    boxSizing: "border-box",
    cursor: "pointer",
    height: selectV2Theme.actionButtons.size[getInputFieldHeightValue(themeMode, inputFieldHeight)](themeMode),
    width: selectV2Theme.actionButtons.size[getInputFieldHeightValue(themeMode, inputFieldHeight)](themeMode),
    margin: 0,
    // padding: `${sizes.xxSmall}px`,
    border: "none",
    borderRadius: selectV2Theme.actionButtons.borderRadius(themeMode),
    fontWeight: 600,
    color: "currentColor",
    justifyContent: "space-around",
    whiteSpace: "nowrap",
    backgroundColor: "transparent",
    transition: `background-color ${motion.animation.default}`,
});

interface ActionButtonProps extends ActionButtonBaseProps {
    isActive?: boolean;
    isHovered?: boolean;
    isOpen?: boolean;
    omitFocusRing?: boolean;
}

export const ActionButton = styled(Clickable)<ActionButtonProps>`
    ${ActionButtonBaseCss}

    ${({ omitFocusRing, themeMode }) =>
        omitFocusRing
            ? ""
            : createFocusRingStyles({
                  theme: themeMode,
                  // We want these outlines to sit within the button's content box.
                  gap: -2,
                  focusSelector: ":focus-visible",
                  componentBorderWidth: 0,
                  componentRadius: formInputTheme.borderRadius(themeMode),
              })}
    background-color: ${({ isHovered, isOpen, isActive, themeMode }) => {
        if (isHovered && !isActive) {
            return `${selectV2Theme.actionButtons.background.hover(themeMode)};`;
        }
        if (isOpen || isActive) {
            return `${selectV2Theme.actionButtons.background.active(themeMode)};`;
        }
        return ``;
    }}
`;
ActionButton.displayName = "ActionButton";
